/**
 * Google Analytics (GA4) + Meta Pixel event tracking utility
 * GA4 Measurement ID: G-40YMV3YVL7
 * Meta Pixel ID: 1497069448742326 (base code in /public/index.html)
 */

const GA_ID = 'G-40YMV3YVL7';
const API = process.env.REACT_APP_BACKEND_URL;

// ==================== FUNNEL VOCABULARY ====================
// Single source of truth shared with the backend. Keeping the names
// stable lets us build a single GA4 report by filtering `event_name =
// funnel_step` and grouping on `step`.
export const FUNNEL_STEPS = Object.freeze({
  LANDING_VIEW: 'landing_view',
  CTA_CLICK: 'cta_click',
  PREVIEW_VIEW: 'preview_view',
  FILE_UPLOAD: 'file_upload',
  GENERATION_START: 'generation_start',
  GENERATION_COMPLETE: 'generation_complete',
  GENERATION_FAILED: 'generation_failed',
  SIGNUP_PROMPT_SHOWN: 'signup_prompt_shown',
  SIGNUP_COMPLETE: 'signup_complete',
  LOGIN_COMPLETE: 'login_complete',
  DOWNLOAD_CLICK: 'download_click',
  DOWNLOAD_COMPLETE: 'download_complete',
});

// Anonymous session id — UUID-ish, persisted in localStorage so a single
// visitor can be stitched through the funnel without auth. Reset by the
// user clearing storage; we don't auto-rotate it.
const _genSessionId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try { return crypto.randomUUID(); } catch (_) {}
  }
  return 'sid_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

const getFunnelSessionId = () => {
  try {
    let sid = localStorage.getItem('pm_funnel_sid');
    if (!sid) {
      sid = _genSessionId();
      localStorage.setItem('pm_funnel_sid', sid);
    }
    return sid;
  } catch (_) {
    return null;
  }
};

function gtag() {
  if (window.gtag) {
    window.gtag(...arguments);
  }
}

/**
 * Meta Pixel helper. `fbq` is loaded by the base snippet in index.html.
 * Wrapped here so missing pixel (e.g. ad-blockers) never breaks the app.
 *
 * INTERNAL USER SUPPRESSION: when the operator has flagged themselves as
 * internal (`localStorage.internal_user='true'` or `?internal=true`), we
 * short-circuit every Pixel call so absolutely no Meta event is sent. This
 * keeps ad-attribution data clean from our own testing traffic.
 */
function fbqSafe() {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  if (window.__pmIsInternalUser) return; // suppress every Pixel event
  try {
    window.fbq(...arguments);
  } catch (_) {
    // never break user flows on tracking errors
  }
}

// Per-page-load dedup so events do not double-fire on React StrictMode renders,
// rapid double-clicks, or polling-driven re-runs. A key is a stable string
// describing the action — re-using the same key in a single browser session
// suppresses repeat firings.
const _firedEvents = new Set();
function _shouldFire(key) {
  if (!key) return true;
  if (_firedEvents.has(key)) return false;
  _firedEvents.add(key);
  return true;
}

// ==================== UNIFIED FUNNEL STEP ====================

/**
 * Track a single funnel step. Fires:
 *  - GA4 `funnel_step` event with `step` parameter (one report covers
 *    everything by filtering this single event name)
 *  - GA4 `funnel_<step>` event for direct-named filtering convenience
 *  - Server-side log to /api/analytics/funnel (immune to ad-blockers)
 *
 * `meta` is optional and gets attached to all three sinks. Internal
 * users are tagged on the server so the admin dashboard can exclude
 * them.
 */
export const trackFunnelStep = (step, meta = {}) => {
  if (!step) return;
  const session_id = getFunnelSessionId();
  const safeMeta = { ...(meta || {}) };

  // GA4 — single event name + parameter style is GA4-native funnel-friendly
  try {
    gtag('event', 'funnel_step', { step, session_id, ...safeMeta });
    gtag('event', `funnel_${step}`, { session_id, ...safeMeta });
  } catch (_) {}

  // Server-side log — fire-and-forget
  try {
    if (API && typeof fetch !== 'undefined') {
      const isInternal = !!(typeof window !== 'undefined' && window.__pmIsInternalUser);
      const path = (typeof window !== 'undefined' && window.location)
        ? (window.location.pathname + window.location.search)
        : null;
      // Use keepalive so the request still leaves on navigation away.
      fetch(`${API}/api/analytics/funnel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          step,
          session_id,
          path,
          is_internal: isInternal,
          meta: safeMeta,
        }),
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_) {}
};

// Track the last Meta-Pixel PageView path so SPA route changes fire exactly
// once even if React StrictMode invokes our effect twice. The base snippet in
// index.html fires the very first PageView on hard load, so we skip the first
// SPA PageView for the initial path to avoid a double-count.
let _lastFbPath = null;
let _initialFbPageViewFired = false;

/** Track a page view (called on every route change) */
export const trackPageView = (path, title) => {
  // Re-assert the internal-user flags on every SPA navigation so every event
  // continues to carry both `user_properties.internal_user='true'` (for
  // audience-based exclusions) and `traffic_type='internal'` (for the GA4
  // native Internal-traffic data filter). Sticky and idempotent.
  try {
    if (typeof window !== 'undefined' && window.__pmIsInternalUser && window.gtag) {
      window.gtag('set', 'user_properties', { internal_user: 'true' });
      window.gtag('set', { traffic_type: 'internal' });
    }
  } catch (_) {}

  gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
  // Meta Pixel — fire PageView on every UNIQUE SPA route. The base snippet
  // already fires once on hard load, so we skip the very first call for that
  // initial path to avoid a double-count. StrictMode-safe via path comparison.
  if (!_initialFbPageViewFired) {
    _initialFbPageViewFired = true;
    _lastFbPath = path;
    return;
  }
  if (path && path !== _lastFbPath) {
    _lastFbPath = path;
    fbqSafe('track', 'PageView');
  }
};

/** Track a custom event */
export const trackEvent = (eventName, params = {}) => {
  gtag('event', eventName, params);
};

// ==================== CTA CLICKS ====================

export const trackCTAClick = (buttonLabel, location) => {
  trackEvent('cta_click', {
    button_label: buttonLabel,
    location: location,
  });
  // Unified funnel step
  trackFunnelStep(FUNNEL_STEPS.CTA_CLICK, { button_label: buttonLabel, location });
  // Meta Pixel custom event — buttons may repeat across the page so we do
  // NOT dedup here; every click is an independent intent signal.
  fbqSafe('trackCustom', 'CTA_Click', {
    button: buttonLabel,
    location: location,
  });
};

// ==================== AUTH EVENTS ====================

/**
 * Track a successful signup as the primary conversion event (Meta Pixel
 * CompleteRegistration + GA4 sign_up + GA4 conversion_signup).
 *
 * Returns a Promise that resolves AFTER a short flush window so callers can
 * `await trackSignup(...)` before navigating away. Without that pause, the
 * Meta Pixel HTTP beacon gets cancelled by the immediate `navigate()` and the
 * event never reaches Meta — that's why CompleteRegistration was silent in
 * the previous implementation.
 */
export const trackSignup = (method = 'email') => {
  trackEvent('sign_up', { method });
  trackEvent('conversion_signup', { method });
  trackFunnelStep(FUNNEL_STEPS.SIGNUP_COMPLETE, { method });

  // Meta Pixel — primary conversion. Session-level dedup so remounts /
  // double React 18 effects can't double-fire CompleteRegistration.
  if (_shouldFire('fb:CompleteRegistration')) {
    fbqSafe('track', 'CompleteRegistration', {
      method: method,
      status: 'success',
    });
  }

  // Give the pixel beacon time to leave the tab before any SPA navigation.
  // 250ms is the value Meta recommends in their SPA integration notes — long
  // enough for sendBeacon / pixel-img to commit, short enough to feel instant.
  return new Promise((resolve) => setTimeout(resolve, 250));
};

export const trackLogin = (method = 'email') => {
  trackEvent('login', { method });
  trackFunnelStep(FUNNEL_STEPS.LOGIN_COMPLETE, { method });
};

// ==================== CORE PRODUCT EVENTS ====================

export const trackFileUpload = (fileType, fileSizeBytes) => {
  trackEvent('file_upload', {
    file_type: fileType,
    file_size_bytes: fileSizeBytes,
  });
  trackFunnelStep(FUNNEL_STEPS.FILE_UPLOAD, {
    file_type: fileType,
    file_size_bytes: fileSizeBytes,
  });
  // Meta Pixel — fire once per real upload action. The caller passes a
  // stable filename or job id when available (we hash it into the key).
  fbqSafe('trackCustom', 'STL_Upload', {
    file_type: fileType,
    file_size_bytes: fileSizeBytes,
  });
};

export const trackSupportGeneration = (mode, treeStyle) => {
  trackEvent('support_generation', {
    mode: mode,
    tree_style: treeStyle,
  });
  trackFunnelStep(FUNNEL_STEPS.GENERATION_START, { mode, tree_style: treeStyle });
  // Meta Pixel — generation can be retried by users on the same page;
  // each click is a real action, so do NOT dedup at the helper level.
  fbqSafe('trackCustom', 'Support_Generation', {
    mode: mode,
    tree_style: treeStyle,
  });
};

export const trackFirstSupportGeneration = () => {
  trackEvent('first_support_generation');
  trackEvent('conversion_first_generation');
};

export const trackDownload = (format, context) => {
  trackEvent('file_download', {
    format: format,
    context: context || 'supports',
  });
  trackFunnelStep(FUNNEL_STEPS.DOWNLOAD_COMPLETE, {
    format,
    context: context || 'supports',
  });
  // Meta Pixel — Download is a strong revenue-intent signal. Dedup per
  // (format, context) so a single download click that triggers multiple
  // axios requests still fires only once.
  const key = `fb:Download:${format || 'unknown'}:${context || 'supports'}`;
  if (_shouldFire(key)) {
    fbqSafe('track', 'Download', {
      content_type: format,
      content_category: context || 'supports',
    });
  }
};

export const trackFirstDownload = (format) => {
  trackEvent('first_download', { format });
  trackEvent('conversion_first_download', { format });
};

// ==================== ENGAGEMENT EVENTS ====================

export const trackCalculatorUse = (filamentCost) => {
  trackEvent('calculator_interaction', {
    filament_cost_per_kg: filamentCost,
  });
};

export const trackDemoClick = () => {
  trackEvent('demo_click');
};

export const trackPricingView = (plan) => {
  trackEvent('pricing_view', { plan });
};

export const trackQuotePDFExport = () => {
  trackEvent('quote_pdf_export');
};
